import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Bot, CreditCard, MapPin, Package, ShoppingCart, Store, Ticket, TrendingUp, Truck, Users } from "lucide-react";
import { DemoCard, useGuide } from "./Guide";
import { MERCH, NYC, STREET } from "./assets";

export function LiveInventoryDemo() {
    const { say } = useGuide();
    const [n, setN] = useState(147);
    useEffect(() => { const t = setInterval(() => setN((x) => (x > 120 ? x - 1 : 147)), 2200); return () => clearInterval(t); }, []);
    return (
        <DemoCard title="Live inventory" hint="watch it tick" testid="demo-inventory">
            <div onMouseEnter={() => say("Stock counts that drop as people buy. Scarcity is the oldest sales trick and it still works.", "inventory")}>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-green">in_stock</p>
                <motion.p key={n} initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl font-black">{n}</motion.p>
                <p className="mt-1 text-xs text-zinc-500">white tees on the shelf · updates itself</p>
            </div>
        </DemoCard>
    );
}

export function AddToCartDemo() {
    const { say } = useGuide();
    const [cart, setCart] = useState(0);
    const [flying, setFlying] = useState(false);
    return (
        <DemoCard title="Add to cart" hint="add one" testid="demo-cart">
            <div className="relative flex items-center gap-3">
                <img src={MERCH[1]} alt="" className="h-16 w-16 rounded-xl object-cover" />
                <div className="flex-1">
                    <p className="text-sm font-bold">Heavyweight tee</p>
                    <p className="font-mono text-sm text-brand-green">$18.00</p>
                </div>
                <button onClick={() => { setCart(cart + 1); setFlying(true); setTimeout(() => setFlying(false), 700); say("Real cart, real checkout, real Stripe. Your customer pays before they hang up the phone.", "cart"); }}
                    className="rounded-xl bg-brand-green px-4 py-2.5 text-xs font-black uppercase tracking-wide text-black" data-testid="demo-cart-add">Add</button>
                <span className="relative">
                    <ShoppingCart size={20} className="text-zinc-400" />
                    {cart > 0 && <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-brand-green font-mono text-[11px] font-black text-black">{cart}</span>}
                </span>
                <AnimatePresence>
                    {flying && <motion.span initial={{ x: -120, y: 0, opacity: 1 }} animate={{ x: 0, y: -14, opacity: 0 }} transition={{ duration: 0.7 }}
                        className="pointer-events-none absolute right-0 h-3 w-3 rounded-full bg-brand-green" />}
                </AnimatePresence>
            </div>
        </DemoCard>
    );
}

export function PricingTableDemo() {
    const { say } = useGuide();
    const [yearly, setYearly] = useState(false);
    const plans = [["Kiosk", 0], ["Storefront", 20], ["Warehouse", 50]];
    return (
        <DemoCard title="Pricing table" hint="monthly / yearly" span="lg:col-span-2" testid="demo-pricing">
            <button onClick={() => { setYearly(!yearly); say("One toggle, three prices re-calculated. These are our real mall rates, by the way — tap The Mall in the nav.", "pricing"); }}
                className="mb-3 rounded-full border border-white/15 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-300" data-testid="demo-pricing-toggle">
                {yearly ? "Yearly · 2 months free" : "Monthly"}
            </button>
            <div className="grid gap-3 sm:grid-cols-3">
                {plans.map(([name, price], i) => (
                    <div key={name} className={`rounded-2xl border p-4 ${i === 1 ? "border-brand-green/50 bg-brand-green/[0.07]" : "border-white/10"}`}>
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-blue">{name}</p>
                        <p className="mt-2 font-display text-2xl font-black">{price === 0 ? "Free" : `$${yearly ? price * 5 : price}`}{price !== 0 && <span className="text-xs text-zinc-500">/{yearly ? "yr" : "mo"}</span>}</p>
                        <p className="mt-2 text-xs text-zinc-400">{["A spot in the mall", "Your own shopfront", "Full department"][i]}</p>
                    </div>
                ))}
            </div>
        </DemoCard>
    );
}

export function ConfiguratorDemo() {
    const { say } = useGuide();
    const [color, setColor] = useState("#ffffff");
    const [size, setSize] = useState("L");
    const colors = ["#ffffff", "#111111", "#2563EB", "#7dd3fc", "#f87171"];
    return (
        <DemoCard title="Product configurator" hint="pick a color" testid="demo-configurator">
            <div className="flex items-center gap-4">
                <div className="relative h-24 w-24 shrink-0 rounded-xl border border-white/10" style={{ background: color }}>
                    <span className="absolute inset-0 flex items-center justify-center font-display text-xs font-black" style={{ color: color === "#111111" ? "#fff" : "#111" }}>{size}</span>
                </div>
                <div>
                    <div className="flex gap-2">
                        {colors.map((c) => (
                            <button key={c} onClick={() => { setColor(c); say("Live product previews — color, size, placement. They see it before they buy it, so they buy it.", "configurator"); }}
                                className={`h-6 w-6 rounded-full border-2 ${color === c ? "border-brand-green" : "border-white/25"}`} style={{ background: c }} data-testid={`demo-config-color-${c.slice(1)}`} />
                        ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                        {["S", "M", "L", "XL"].map((s) => (
                            <button key={s} onClick={() => setSize(s)} className={`rounded-lg border px-2.5 py-1 text-xs font-bold ${size === s ? "border-brand-green text-brand-green" : "border-white/15 text-zinc-400"}`} data-testid={`demo-config-size-${s}`}>{s}</button>
                        ))}
                    </div>
                </div>
            </div>
        </DemoCard>
    );
}

export function CouponDemo() {
    const { say } = useGuide();
    const [code, setCode] = useState("");
    const ok = code.toUpperCase() === "GANG";
    return (
        <DemoCard title="Coupon codes" hint="try GANG" testid="demo-coupon">
            <div className="flex gap-2">
                <input value={code} onChange={(e) => { setCode(e.target.value); if (e.target.value.toUpperCase() === "GANG") say("Promo codes, member pricing, first-order discounts — all automatic, no math on your end.", "coupon"); }}
                    className="w-full rounded-xl border border-white/15 bg-brand-ink px-3.5 py-2.5 text-sm uppercase" placeholder="Code" data-testid="demo-coupon-input" />
                <span className={`flex items-center gap-1.5 rounded-xl px-3 text-xs font-black uppercase ${ok ? "bg-brand-green text-black" : "border border-white/15 text-zinc-500"}`}>
                    <Ticket size={13} /> {ok ? "-15%" : "apply"}
                </span>
            </div>
            <p className="mt-2 text-xs text-zinc-500">{ok ? "Gang member pricing applied automatically." : "Hint: the code is GANG"}</p>
        </DemoCard>
    );
}

export function BookingDemo() {
    const { say } = useGuide();
    const [slot, setSlot] = useState(null);
    const slots = ["9:00", "10:00", "11:00", "1:00", "2:00", "3:00"];
    return (
        <DemoCard title="Booking calendar" hint="grab a slot" testid="demo-booking">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">tomorrow</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
                {slots.map((s, i) => (
                    <button key={s} onClick={() => { setSlot(s); say("Slots fill in real time — no back-and-forth texts, no double bookings.", "booking"); }}
                        disabled={i === 2}
                        className={`rounded-xl border px-2 py-2.5 text-xs font-bold transition-colors ${slot === s ? "border-brand-green bg-brand-green text-black" : i === 2 ? "border-white/8 text-zinc-600 line-through" : "border-white/12 text-zinc-300 hover:border-brand-green/50"}`}
                        data-testid={`demo-booking-${i}`}>{s}</button>
                ))}
            </div>
            {slot && <p className="mt-2.5 text-xs text-brand-green">Booked {slot} — confirmation text sent.</p>}
        </DemoCard>
    );
}

export function ChatQuoteDemo() {
    const { say } = useGuide();
    const [log, setLog] = useState([{ me: false, t: "Hey — I'm the shop bot. Ask me a price." }]);
    const ask = (q) => {
        const answers = {
            "how much for 20 shirts?": "~$14/pc after setup on white tees. Want me to hold a same-day slot?",
            "do you do banners?": "Yep — 3x6ft vinyl, $95, ready in 3–5 days.",
            "open now?": "We're online 24/7. Pickup window today closes at 7pm.",
        };
        setLog((l) => [...l, { me: true, t: q }, { me: false, t: answers[q] }]);
        say("An AI assistant that quotes, answers and books while you're printing. This is the part that didn't exist two years ago.", "chat");
    };
    return (
        <DemoCard title="AI chat that quotes" hint="ask something" span="lg:col-span-2" testid="demo-chat">
            <div className="flex h-40 flex-col gap-2 overflow-y-auto rounded-xl border border-white/10 bg-black/50 p-3">
                {log.map((m, i) => (
                    <p key={i} className={`max-w-[85%] rounded-xl px-3 py-2 text-xs ${m.me ? "self-end bg-brand-green text-black" : "bg-white/[0.07] text-zinc-200"}`}>
                        {!m.me && <Bot size={11} className="mr-1 inline text-brand-green" />}{m.t}
                    </p>
                ))}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-2">
                {["how much for 20 shirts?", "do you do banners?", "open now?"].map((q) => (
                    <button key={q} onClick={() => ask(q)} className="rounded-full border border-white/15 px-3 py-1.5 text-[11px] text-zinc-300 hover:border-brand-green/50 hover:text-brand-green" data-testid={`demo-chat-q-${q.slice(0, 6)}`}>{q}</button>
                ))}
            </div>
        </DemoCard>
    );
}

export function MapDemo() {
    const { say } = useGuide();
    const [pin, setPin] = useState(1);
    const spots = [["1309 78th St · the shop", 42, 58], ["Brooklyn Bridge", 22, 30], ["Verrazzano", 70, 78]];
    return (
        <DemoCard title="Map with real pins" hint="tap a pin" testid="demo-map">
            <div className="relative h-32 overflow-hidden rounded-2xl border border-white/10">
                <img src={NYC[3]} alt="" className="h-full w-full object-cover opacity-45" />
                {spots.map(([label, x, y], i) => (
                    <button key={label} onClick={() => { setPin(i); say("Live maps, directions, delivery zones. Perfect for the mall idea — every vendor on one map.", "map"); }}
                        className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full p-1.5 ${pin === i ? "bg-brand-green text-black" : "bg-black/80 text-brand-green"}`}
                        style={{ left: `${x}%`, top: `${y}%` }} data-testid={`demo-map-pin-${i}`}><MapPin size={13} /></button>
                ))}
                <p className="absolute inset-x-0 bottom-0 bg-black/80 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-brand-green">{spots[pin][0]}</p>
            </div>
        </DemoCard>
    );
}

export function DashboardDemo() {
    const { say } = useGuide();
    const [range, setRange] = useState("7d");
    const bars = { "7d": [40, 65, 30, 80, 55, 95, 70], "30d": [30, 50, 70, 45, 85, 60, 100] };
    return (
        <DemoCard title="Owner dashboard" hint="switch range" testid="demo-dashboard">
            <div className="flex gap-2">
                {["7d", "30d"].map((r) => (
                    <button key={r} onClick={() => { setRange(r); say("Your own dashboard — what sold, who reordered, what to print next. You stop guessing.", "dashboard"); }}
                        className={`rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-widest ${range === r ? "bg-brand-green text-black" : "border border-white/15 text-zinc-400"}`} data-testid={`demo-dash-${r}`}>{r}</button>
                ))}
            </div>
            <div className="mt-3 flex h-20 items-end gap-1.5">
                {bars[range].map((h, i) => (
                    <motion.span key={`${range}-${i}`} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: i * 0.05 }}
                        className="flex-1 rounded-t bg-gradient-to-t from-brand-green/30 to-brand-green" />
                ))}
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-xs text-brand-green"><TrendingUp size={12} /> +32% vs last {range}</p>
        </DemoCard>
    );
}

export function NotificationDemo() {
    const { say } = useGuide();
    const [toasts, setToasts] = useState([]);
    const fire = () => {
        const msgs = ["New order · 24 white tees", "Payment received · $412", "Proof approved · #3921", "New review · 5 stars"];
        const t = { id: Date.now(), text: msgs[Math.floor(Math.random() * msgs.length)] };
        setToasts((x) => [...x, t]);
        say("Real-time alerts on your phone the second money moves. No refreshing, no wondering.", "notification");
        setTimeout(() => setToasts((x) => x.filter((y) => y.id !== t.id)), 2600);
    };
    return (
        <DemoCard title="Live notifications" hint="ring it" testid="demo-notifications">
            <button onClick={fire} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-bold hover:border-brand-green/50" data-testid="demo-notify-btn">
                <Bell size={15} /> Simulate an order
            </button>
            <div className="mt-3 space-y-2">
                <AnimatePresence>
                    {toasts.map((t) => (
                        <motion.p key={t.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                            className="rounded-xl border border-brand-green/30 bg-brand-green/[0.08] px-3 py-2 text-xs text-zinc-100">{t.text}</motion.p>
                    ))}
                </AnimatePresence>
            </div>
        </DemoCard>
    );
}

export function OrderTrackerDemo() {
    const { say } = useGuide();
    const [step, setStep] = useState(1);
    const stages = [["Received", Package], ["Printing", Store], ["Ready", Truck], ["Picked up", CreditCard]];
    return (
        <DemoCard title="Order tracker" hint="advance it" span="lg:col-span-2" testid="demo-tracker">
            <div className="flex items-center">
                {stages.map(([label, Icon], i) => (
                    <div key={label} className="flex flex-1 items-center">
                        <button onClick={() => { setStep(i); say("Customers track their own job. Your phone stops ringing with 'is it ready yet?'", "tracker"); }}
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${i <= step ? "bg-brand-green text-black" : "border border-white/15 text-zinc-500"}`}
                            data-testid={`demo-tracker-${i}`}><Icon size={15} /></button>
                        {i < stages.length - 1 && <span className={`h-0.5 flex-1 ${i < step ? "bg-brand-green" : "bg-white/12"}`} />}
                    </div>
                ))}
            </div>
            <p className="mt-3 text-sm">{stages[step][0]} — <span className="text-zinc-400">customer sees this live, gets a text at every step.</span></p>
        </DemoCard>
    );
}

export function ReviewsDemo() {
    const { say } = useGuide();
    const rows = [
        ["Maya · Bushwick", "Fast, clean, cheaper than expected."],
        ["Dej · Sunset Park", "Ordered at noon, wore it that night."],
        ["Rob · Bay Ridge", "My site paid for itself in three weeks."],
    ];
    const [i, setI] = useState(0);
    useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % rows.length), 3400); return () => clearInterval(t); }, []);
    return (
        <DemoCard title="Reviews on rotation" hint="hover" testid="demo-reviews">
            <div onMouseEnter={() => say("Fresh customer voices rotating forever. Trust, without you asking for it twice.", "reviews")} className="h-20">
                <AnimatePresence mode="wait">
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                        <p className="text-sm italic text-zinc-100">“{rows[i][1]}”</p>
                        <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-brand-green">{rows[i][0]}</p>
                    </motion.div>
                </AnimatePresence>
            </div>
        </DemoCard>
    );
}

export function PortalDemo() {
    const { say } = useGuide();
    const [open, setOpen] = useState(false);
    const orders = [["#3921", "12 shirts"], ["#3908", "Vinyl banner"], ["#3874", "DTF sheet"]];
    return (
        <DemoCard title="Customer portal" hint="log in" testid="demo-portal">
            <button onClick={() => { setOpen(!open); say("Every order, receipt and one-tap reorder behind one login. Repeat business on rails.", "portal"); }}
                className="w-full rounded-xl border border-white/15 px-4 py-2.5 text-sm font-bold hover:border-brand-green/50" data-testid="demo-portal-btn">
                <Users size={14} className="mr-1.5 inline" /> {open ? "user: dej_l" : "Log in as a customer"}
            </button>
            {open && (
                <div className="mt-3 space-y-2">
                    {orders.map(([id, what]) => (
                        <p key={id} className="flex items-center justify-between rounded-xl bg-white/[0.04] px-3 py-2 text-xs">
                            <span className="font-mono text-zinc-400">{id}</span><span>{what}</span><span className="font-mono text-brand-green">reorder ↺</span>
                        </p>
                    ))}
                </div>
            )}
        </DemoCard>
    );
}

export function MallDemo() {
    const { say } = useGuide();
    const [hover, setHover] = useState(null);
    const shops = [["Print kiosk", "Free"], ["Sign shop", "$20/mo"], ["Embroidery", "$20/mo"], ["Blank warehouse", "$50/mo"]];
    return (
        <DemoCard title="13thav (coming)" hint="hover a unit" span="lg:col-span-2" testid="demo-mall">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {shops.map(([name, price], i) => (
                    <button key={name} onMouseEnter={() => { setHover(i); say("This is where we're going: one printing mall. Vendors rent a virtual kiosk, store or warehouse — customers shop the whole trade in one place.", "mall"); }}
                        className={`relative overflow-hidden rounded-2xl border p-3 text-left transition-colors ${hover === i ? "border-brand-green bg-brand-green/[0.1]" : "border-white/10"}`}
                        data-testid={`demo-mall-${i}`}>
                        <img src={STREET[i % STREET.length]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
                        <span className="relative block font-display text-sm font-black">{name}</span>
                        <span className="relative mt-1 block font-mono text-[11px] uppercase tracking-widest text-brand-green">{price}</span>
                    </button>
                ))}
            </div>
        </DemoCard>
    );
}
